import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getOrCreateDeviceId } from '../utils/deviceId';
import { AppConfig } from '../config/AppConfig';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { fetchWithRetry } from '../utils/fetchWithRetry';

const DEVICE_REGISTRATION_KEY = 'eduwrite_device_registration';

export interface DeviceRegistrationState {
    deviceId: string | null;
    isRegistered: boolean;
    orgId: string | null;
    schoolName: string | null;
    deviceToken: string | null;
    serverUrl: string | null;
    isLoading: boolean;
    error: string | null;

    initializeDevice: () => Promise<void>;
    checkRegistrationWithServer: (customServerUrl?: string) => Promise<boolean>;
    setDeviceRegistration: (data: { orgId: string; schoolName: string; deviceToken?: string; serverUrl?: string }) => Promise<void>;
    clearDeviceRegistration: () => Promise<void>;
}

export const useDeviceStore = create<DeviceRegistrationState>((set, get) => ({
    deviceId: null,
    isRegistered: false,
    orgId: null,
    schoolName: null,
    deviceToken: null,
    serverUrl: null,
    isLoading: false,
    error: null,

    initializeDevice: async () => {
        try {
            set({ isLoading: true, error: null });
            const deviceId = await getOrCreateDeviceId();
            set({ deviceId });

            // Attempt to hydrate from local storage first for instant UI response
            const cachedRaw = await AsyncStorage.getItem(DEVICE_REGISTRATION_KEY);
            if (cachedRaw) {
                try {
                    const cached = JSON.parse(cachedRaw);
                    set({
                        isRegistered: cached.isRegistered === true,
                        orgId: cached.orgId || null,
                        schoolName: cached.schoolName || null,
                        deviceToken: cached.deviceToken || null,
                        serverUrl: cached.serverUrl || null,
                    });
                } catch {
                    // Ignore JSON parse error on cache
                }
            }

            // Next, asynchronously verify with the sync-server
            await get().checkRegistrationWithServer();
        } catch (err: unknown) {
            console.error('[DeviceStore] Failed to initialize device:', err);
            set({ isLoading: false, error: (err as Error).message });
        }
    },

    checkRegistrationWithServer: async (customServerUrl?: string): Promise<boolean> => {
        const { deviceId } = get();
        if (!deviceId) return false;

        set({ isLoading: true });
        try {
            const serverUrl = customServerUrl || StrokeSyncService.getServerUrl() || AppConfig.DEFAULT_SERVER_URL;
            const apiBase = String(serverUrl).replace(/\/+$/, '');

            const res = await fetchWithRetry(`${apiBase}/api/devices/status?deviceId=${encodeURIComponent(deviceId)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                timeoutMs: 6000,
                maxRetries: 2,
            });

            if (!res.ok) {
                set({ isLoading: false });
                return get().isRegistered;
            }

            const data = await res.json();
            if (data.isRegistered) {
                const regData = {
                    deviceId,
                    isRegistered: true,
                    orgId: data.orgId,
                    schoolName: data.schoolName,
                    deviceToken: data.deviceToken || get().deviceToken,
                    serverUrl,
                };
                await AsyncStorage.setItem(DEVICE_REGISTRATION_KEY, JSON.stringify(regData));
                set({
                    ...regData,
                    isLoading: false,
                    error: null,
                });
                return true;
            } else {
                set({
                    isRegistered: false,
                    isLoading: false,
                });
                return false;
            }
        } catch (err) {
            console.warn('[DeviceStore] Server check skipped or offline:', err);
            // In offline scenarios, retain cached registration
            set({ isLoading: false });
            return get().isRegistered;
        }
    },

    setDeviceRegistration: async (data) => {
        const { deviceId } = get();
        const serverUrl = data.serverUrl || StrokeSyncService.getServerUrl() || AppConfig.DEFAULT_SERVER_URL;
        const regData = {
            deviceId,
            isRegistered: true,
            orgId: data.orgId,
            schoolName: data.schoolName,
            deviceToken: data.deviceToken || null,
            serverUrl,
        };

        await AsyncStorage.setItem(DEVICE_REGISTRATION_KEY, JSON.stringify(regData));
        set({
            ...regData,
            error: null,
        });
    },

    clearDeviceRegistration: async () => {
        await AsyncStorage.removeItem(DEVICE_REGISTRATION_KEY);
        set({
            isRegistered: false,
            orgId: null,
            schoolName: null,
            deviceToken: null,
        });
    },
}));
